import { Box, Stack, Typography, useTheme } from "@mui/material";
import androidQr from "assets/images/androidQr.png";
import iosQr from "assets/images/iosQr.png";
import teamsDownloadQr from "assets/images/teamsDownloadQr.png";
import {
  Button,
  CheckCircleIcon,
  ControlcenterIcon,
  CopyIcon,
  ExternalLinkIcon,
  WebPortalIcon,
} from "components";
import {
  useGetAllCertificates,
  useGlobalStore,
  useNonPersistantMainStore,
  usePermissionStore,
} from "controller";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS } from "shared";
import androidLogo from "./assets/images/androidLogo.webp";
import appleLogo from "./assets/images/appleLogo.webp";
import msTeamsLogo from "./assets/images/msTeamsLogo.webp";
import windowsLogo from "./assets/images/windowsLogo.webp";
import { ContactAccountOwnerNotice } from "./ContactAccountOwnerNotice";
import { DownloadDrawerAccordion } from "./DownloadDrawerAccordion";
import { DownloadDrawerRadioBtns } from "./DownloadDrawerRadioBtns";
import { QrCodeComponent } from "./QrCodeComponent";
import { useEnterpriseAppQrCodes } from "./useEnterpriseAppQrCodes";

export const AppsForMspCustomers = ({
  closeDownloadDrawer,
}: {
  closeDownloadDrawer: () => void;
}) => {
  const theme = useTheme();

  const [currentAccordionIndex, setCurrentAccordionIndex] = useState(-1);
  const [showCopyWebPortalLinkSuccess, setShowCopyWebPortalLinkSuccess] =
    useState(false);

  const mspId = useGlobalStore((state) => state.mspId);
  const isEnterprise = useGlobalStore((state) => state.isEnterprise);
  const { data: certificates, isLoading: isCertificatesLoading } =
    useGetAllCertificates(mspId || "");

  // Only enterprise MSPs have their own builds — everyone else scans the
  // static DeskDay QRs, so nothing needs fetching for them.
  const [isAndroidExpanded, setIsAndroidExpanded] = useState(false);
  const [isIosExpanded, setIsIosExpanded] = useState(false);
  const {
    androidQrImageUrl,
    iosQrImageUrl,
    isLoading: isEnterpriseAppLoading,
    isError: isEnterpriseAppError,
    refetch: refetchEnterpriseApp,
    hasNoAndroidBuild,
    hasNoIosBuild,
  } = useEnterpriseAppQrCodes({
    mspId: mspId || "",
    enabled: isEnterprise && (isAndroidExpanded || isIosExpanded),
  });

  const itConnectLink =
    certificates?.find((cert) => cert.vanityUrlType === "agent_web_app")
      ?.domainName || "https://itconnect.deskday.ai";

  const WindowsRadioMenuData = [
    {
      label: "EXE",
      value: "win_exe",
    },
    {
      label: "MSI",
      value: "win_msi",
    },
  ];
  const AppleRadioMenuData = [
    {
      label: "Mac with Apple Silicon",
      value: "apple_m",
    },
    {
      label: "Mac with Intel chip",
      value: "apple_intel",
    },
  ];

  // Enterprise MSPs scan a QR for their own build; everyone else scans the
  // static DeskDay one. A successful response with no URL means that platform
  // has no build yet, which only the account owner can change.
  const renderPlatformContent = ({
    staticQrCode,
    enterpriseQrImageUrl,
    hasNoEnterpriseBuild,
  }: {
    staticQrCode: string;
    enterpriseQrImageUrl: string;
    hasNoEnterpriseBuild: boolean;
  }) => {
    if (!isEnterprise) {
      return <QrCodeComponent qrCode={staticQrCode} />;
    }
    if (hasNoEnterpriseBuild) {
      return <ContactAccountOwnerNotice />;
    }
    return (
      <QrCodeComponent
        qrCode={enterpriseQrImageUrl}
        isLoading={isEnterpriseAppLoading}
        isError={isEnterpriseAppError}
        onRetry={() => refetchEnterpriseApp()}
      />
    );
  };

  const androidContent = renderPlatformContent({
    staticQrCode: androidQr,
    enterpriseQrImageUrl: androidQrImageUrl,
    hasNoEnterpriseBuild: hasNoAndroidBuild,
  });
  const iosContent = renderPlatformContent({
    staticQrCode: iosQr,
    enterpriseQrImageUrl: iosQrImageUrl,
    hasNoEnterpriseBuild: hasNoIosBuild,
  });

  const CHANNELS = [
    {
      logo: windowsLogo,
      title: "Windows",
      key: "windows",
      content: (
        <DownloadDrawerRadioBtns
          radioMenuData={WindowsRadioMenuData}
          closeDownloadDrawer={closeDownloadDrawer}
        />
      ),
    },
    {
      logo: appleLogo,
      title: "macOS",
      key: "macos",
      content: (
        <DownloadDrawerRadioBtns
          radioMenuData={AppleRadioMenuData}
          closeDownloadDrawer={closeDownloadDrawer}
        />
      ),
    },
    {
      logo: androidLogo,
      title: "Android",
      key: "android",
      content: androidContent,
    },
    {
      logo: appleLogo,
      title: "iOS",
      key: "ios",
      content: iosContent,
    },
    {
      logo: msTeamsLogo,
      title: "MS Teams",
      key: "msteams",
      content: isEnterprise ? (
        <ContactAccountOwnerNotice />
      ) : (
        <QrCodeComponent
          qrCode={teamsDownloadQr}
          closeDownloadDrawer={closeDownloadDrawer}
        />
      ),
    },
    {
      logo: <WebPortalIcon color="primary" />,
      isLogoIcon: true,
      title: "Web Portal",
      key: "webportal",
      content: (
        <Stack gap="24px">
          <Stack gap="4px">
            <Typography variant="h6">
              Access IT-Connect from any browser.
            </Typography>
            <Typography variant="body-medium" color="lightBg.low">
              No installs, no setup - just log in to the web portal and start
              resolving tickets in seconds.
            </Typography>
          </Stack>
          <Stack direction="row" alignItems={"center"} gap="8px">
            <Button
              endIcon={<ExternalLinkIcon />}
              onClick={() => {
                const url =
                  itConnectLink.startsWith("http://") ||
                  itConnectLink.startsWith("https://")
                    ? itConnectLink
                    : `https://${itConnectLink}`;
                window.open(url, "_blank");
              }}
              loading={isCertificatesLoading}
            >
              Open
            </Button>
            <Button
              variant="secondary"
              endIcon={
                showCopyWebPortalLinkSuccess ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <CopyIcon />
                )
              }
              sx={{
                minWidth: showCopyWebPortalLinkSuccess ? "124px" : "112px",
              }}
              onClick={() => {
                navigator.clipboard.writeText(itConnectLink);
                setShowCopyWebPortalLinkSuccess(true);
                setTimeout(() => setShowCopyWebPortalLinkSuccess(false), 2000);
              }}
              loading={isCertificatesLoading}
            >
              {showCopyWebPortalLinkSuccess ? "Link Copied" : "Copy Link"}
            </Button>
          </Stack>
        </Stack>
      ),
    },
  ];

  const navigate = useNavigate();
  const hasPermissions = usePermissionStore((state) => state.hasPermissions);
  const featureFlagsFromStore = useNonPersistantMainStore(
    (state) => state.featureFlags,
  );
  const isShowOnboardingScreen =
    featureFlagsFromStore?.show_onboarding_screen === true;

  return (
    <Stack px="24px" height="100%">
      {hasPermissions([PERMISSIONS.CC.CC_CHANNEL_EMAIL_RW]) &&
        !isShowOnboardingScreen && (
          <Stack
            direction="row"
            alignItems={"center"}
            gap="4px"
            bgcolor={theme.palette.secondary["purple-tint-3"]}
            my="18px"
            borderRadius={"6px"}
            p="12px 16px"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              navigate("/app/control-center/channels/email");
              closeDownloadDrawer();
            }}
          >
            <ControlcenterIcon
              color="primary"
              sx={{
                width: "24px",
                height: "24px",
              }}
            />
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "400",
                lineHeight: "24px",
                fontFamily: "SegoeRegular",
              }}
              color="primary.main"
            >
              Setup Your Support Email
            </Typography>
          </Stack>
        )}

      <Box sx={{ overflowY: "scroll", flexGrow: 1 }}>
        {CHANNELS.map(
          ({ logo, isLogoIcon = false, title, content, key }, index) => (
            <DownloadDrawerAccordion
              key={key}
              logo={logo}
              isLogoIcon={isLogoIcon}
              title={title}
              content={content}
              expanded={currentAccordionIndex === index}
              onChange={(expanded) => {
                setCurrentAccordionIndex(expanded ? index : -1);
                if (key === "android" && expanded) {
                  setIsAndroidExpanded(true);
                }
                if (key === "ios" && expanded) {
                  setIsIosExpanded(true);
                }
              }}
            />
          ),
        )}
      </Box>
      <Box py="16px" flexShrink={0} display="flex">
        <Typography
          sx={{ cursor: "pointer" }}
          onClick={() => {
            window.open(
              "https://support.deskday.com/en/articles/8235973-installing-the-it-connect",
              "_blank",
            );
          }}
          variant="link-small"
        >
          Discover IT-Connect
        </Typography>
      </Box>
    </Stack>
  );
};
